import React, { type ReactNode, useState } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import IconTile from '@atlaskit/icon/icon-tile';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from '../messages';

const styles = cssMap({
	cardWrapper: {
		borderRadius: token('radius.xlarge'),
		border: `${token('border.width', '1px')} solid ${token('color.border', '#DFE1E6')}`,
	},
	cardHeader: {
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
	},
	cardHeaderBorder: {
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
});

type CardWrapperProps = {
	subtitle?: ReactNode;
	headerActionOverride?: ReactNode;
	children?: ReactNode;
	isExpandable?: boolean;
	testId?: string;
};

export const CardWrapper = ({
	subtitle,
	headerActionOverride,
	children,
	isExpandable = false,
	testId,
}: CardWrapperProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(isExpandable);

	return (
		<Stack xcss={styles.cardWrapper} testId={testId}>
			<Box xcss={cx(styles.cardHeader, isExpanded && styles.cardHeaderBorder)} testId="card-header">
				<Inline space="space.150" alignBlock="center" spread="space-between">
					<Inline space="space.150" alignBlock="center">
						<IconTile icon={AiGenerativeCleanupIcon} label="" appearance="gray" size="medium" />
						<Stack>
							<Heading size="xsmall">{formatMessage(messages.cardTitle)}</Heading>
							{subtitle && (
								<Text color="color.text.subtlest" size="small">
									{subtitle}
								</Text>
							)}
						</Stack>
					</Inline>
					{headerActionOverride ||
						(isExpandable && (
							<IconButton
								appearance="subtle"
								spacing="compact"
								icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
								label={
									isExpanded
										? formatMessage(messages.collapseLabel)
										: formatMessage(messages.expandLabel)
								}
								onClick={() => setIsExpanded((prev) => !prev)}
							/>
						))}
				</Inline>
			</Box>
			{isExpanded && children}
		</Stack>
	);
};
